import { Row } from 'react-table';

interface SubRowMatchData {
    /**
     * Count of detections for rows that have been selected by the user to include in the report.
     */
    includedDetections: number;
    /**
     * Number of sub-rows that are matched to the same statement work.
     */
    matchCount: number;
    /**
     * When present, this is the ID of the statement work the sub-rows are matched to.
     */
    matchedWorkId: Maybe<string>;
    /**
     * When present, this is the ID of the statement work the sub-rows are matched to.
     */
    matchedWorkPerformances: number;
    /**
     * When present, this is the title of the statement work the sub-rows are matched to.
     */
    matchedWorkTitle: Maybe<string>;
}

const initialValue: SubRowMatchData = {
    includedDetections: 0,
    matchCount: 0,
    matchedWorkId: null,
    matchedWorkPerformances: 0,
    matchedWorkTitle: null,
};

/**
 * Returns some information about whether a row contains sub-rows that are matched to a statement
 * work.
 * @param subRows - array of subRows
 * @param useCurrentCheckedState - when this is true, we use the `.checked` property to determine
 * whether to count the detections for a {@link TrqkGapChannelAudio}. When this is false, we use the
 * `.include` property in order to target only rows that have been selected in the persisted backend
 * state. This is helpful when establishing a sort order based on the gap, because otherwise the
 * sort order changes when the user selects / de-selects the child of a summarized row.
 * @returns a {@link SubRowMatchData} object
 */
export const getSubRowMatchData = (
    subRows: Row<TrqkGapChannelAudio>[],
    useCurrentCheckedState = true,
): SubRowMatchData =>
    subRows.reduce(
        (acc: SubRowMatchData, subRow: Row<TrqkGapChannelAudio>) => {
            const matchedWorkId = subRow.original?.matched_work_id ?? null;
            const matchedWorkTitle =
                subRow.original?.matched_work_title ?? null;
            const detectionCount = subRow.original?.detection_count ?? 0;
            const matchedWorkPerformances =
                subRow.original?.matched_work_performances ?? 0;
            // The `checked` property is calculated in getTrqkGapChannelAudios from the current state
            // in the UI, whereas the `include` property is persisted in the underlying data.
            const checked: boolean = useCurrentCheckedState
                ? subRow.isSelected ?? false
                : subRow?.original?.include ?? false;

            if (
                acc.matchedWorkId === null &&
                matchedWorkId !== null &&
                matchedWorkTitle !== null
            ) {
                acc.matchedWorkId = matchedWorkId;
                acc.matchedWorkTitle = matchedWorkTitle;
                acc.matchCount += 1;
                acc.matchedWorkPerformances = matchedWorkPerformances;

                if (checked) {
                    acc.includedDetections += detectionCount;
                }
            } else if (
                matchedWorkId !== null &&
                acc.matchedWorkId === matchedWorkId
            ) {
                acc.matchCount += 1;

                if (checked) {
                    acc.includedDetections += detectionCount;
                }
            } else if (
                matchedWorkId !== null &&
                acc.matchedWorkId !== matchedWorkId
            ) {
                // This case indicates a mismatch in the data itself. We should never end up here,
                // but if we do, we are going to reset everything to null and the UI will reflect
                // that data is missing.
                return { ...initialValue };
            }

            return acc;
        },
        { ...initialValue },
    );
